/**
 * enums
 */
import { IMediaSize } from "@core/enums/media-sizes";
import { ThemeColorsVariants, IThemeColorVariant } from "@core/enums/theme-colors-variants";

/**
 * utilities
 */
import { AppUtils } from "@core/utilities";

export abstract class SharedUtils {

    /**
     * @description Generates CSS class style for theme color variants
     * @param variant - Theme color variant (enum or string)
     * @param host - CSS class prefix (e.g., 'btn', 'alert', 'text')
     * @returns Formatted CSS class string
     * @example
     * ```typescript
     * SharedUtils.generateVariantClassStyle(ThemeColorsVariants.Primary, 'btn') // 'btn-primary'
     * SharedUtils.generateVariantClassStyle('success', 'alert') // 'alert-success'
     * ```
     */
    public static generateVariantClassStyle(variant: ThemeColorsVariants | IThemeColorVariant, host: string): string {
        return `${host}-${variant}`;
    }

    /**
     * @description Generates CSS class style for size variants
     * @param size - Size variant (e.g., 'sm', 'lg')
     * @param host - CSS class prefix (e.g., 'btn', 'alert', 'text')
     * @returns Formatted CSS class string
     * @example
     */
    public static generateSizeClassStyle(size: IMediaSize, host: string): string {
        return `${host}-${size}`;
    }

    /**
     * @description Safely gets a nested value from an object by dot-path.
     * @example SharedUtils.getByPath(row, 'user.profile.name')
     */
    public static getByPath<TResult = unknown>(row: unknown, field: string): TResult | null {
        if (typeof row !== 'object') return null;
        if (typeof field !== 'string' || field.trim().length === 0) return null;

        try {
            const value = AppUtils.chainByReducerFn<Record<string, unknown>, TResult>(field, row as Record<string, unknown>);
            return value ?? null;
        } catch {
            return null;
        }
    }

    /**
     * @description Normalizes unknown values to comparable values for sorting.
     * - number/numeric-string => number
     * - Date/date-string => epoch ms
     * - string => lowercase trimmed string
     * - boolean => boolean
     * - null/undefined/invalid => null
     */
    public static normalizeSortValue(value: unknown): number | string | boolean | null {
        if (value == null) return null;

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (value instanceof Date) {
            const time = value.getTime();
            return Number.isFinite(time) ? time : null;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'bigint') {
            const asNumber = Number(value);
            return Number.isFinite(asNumber) ? asNumber : null;
        }

        if (typeof value === 'string') {
            const normalized = value.trim();
            if (normalized.length === 0) return null;

            const asNumber = Number(normalized);
            if (!Number.isNaN(asNumber) && Number.isFinite(asNumber)) {
                return asNumber;
            }

            const asDate = Date.parse(normalized);
            if (!Number.isNaN(asDate)) {
                return asDate;
            }

            return normalized.toLowerCase();
        }

        return String(value).toLowerCase();
    }

    /**
     * @description Compares two normalized sort values.
     * @returns -1 if a < b, 1 if a > b, 0 if equal.
     * @note Null values are sorted last by default.
     */
    public static compareNormalized(
        a: number | string | boolean | null,
        b: number | string | boolean | null
    ): number {
        if (a == null && b == null) return 0;
        if (a == null) return 1;
        if (b == null) return -1;

        if (typeof a === 'number' && typeof b === 'number') {
            if (a === b) return 0;
            return a < b ? -1 : 1;
        }

        if (typeof a === 'boolean' && typeof b === 'boolean') {
            const diff = Number(a) - Number(b);
            if (diff === 0) return 0;
            return diff < 0 ? -1 : 1;
        }

        const left = String(a);
        const right = String(b);

        return left.localeCompare(right, undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }

    /**
     * @description Compares two rows by one field and sort order.
     * @param rowA - First row
     * @param rowB - Second row
     * @param field - Dot path field (e.g. user.name)
     * @param order - Sort direction
     */
    public static compareRowsByField(
        rowA: unknown,
        rowB: unknown,
        field: string,
        order: 'asc' | 'desc'
    ): number {
        const valueA = this.getByPath(rowA, field);
        const valueB = this.getByPath(rowB, field);

        const normalizedA = this.normalizeSortValue(valueA);
        const normalizedB = this.normalizeSortValue(valueB);

        const result = this.compareNormalized(normalizedA, normalizedB);
        return order === 'desc' ? result * -1 : result;
    }

    /**
     * @description Sorts rows by sort meta rules (supports single and multiple sorting).
     * @note Stable sort: original order is preserved when rows are equal across all sort rules.
     */
    public static sortRowsByMeta<TRow>(
        data: TRow[],
        sortMeta: { column: string, order: 'asc' | 'desc' }[]
    ): TRow[] {
        if (!Array.isArray(data) || data.length <= 1) return data ?? [];
        if (!Array.isArray(sortMeta) || sortMeta.length === 0) return [...data];

        const validSortMeta = sortMeta.filter(meta => {
            return typeof meta?.column === 'string' && meta.column.trim().length > 0;
        });

        if (validSortMeta.length === 0) return [...data];

        return data
            .map((row, index) => ({ row, index }))
            .sort((a, b) => {
                for (const meta of validSortMeta) {
                    const compared = this.compareRowsByField(a.row, b.row, meta.column, meta.order);
                    if (compared !== 0) return compared;
                }

                // Stable fallback: keep original relative order
                return a.index - b.index;
            })
            .map(item => item.row);
    }
}

/**
 * Returns '#656363' or '#ffffff' for readable contrast over `hex`.
 * Uses WCAG perceived-brightness (BT.601 coefficients).
 */
export function contrastColor(hex: string): string {
    const h = hex.replace('#', '');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 150 ? '#656363' : '#ffffff';
}

/**
 * Blends `hex` toward white by `percent` (0–100).
 * Example: 40 → 40% lighter (GitHub-style label tint).
 */
export function lightenHex(hex: string, percent: number): string {
    const f = parseInt(hex.slice(1), 16);
    const r = f >> 16, g = (f >> 8) & 0xff, b = f & 0xff;
    return '#' + (
        0x1000000 +
        (Math.round((255 - r) * percent / 100) + r) * 0x10000 +
        (Math.round((255 - g) * percent / 100) + g) * 0x100 +
        (Math.round((255 - b) * percent / 100) + b)
    ).toString(16).slice(1);
}
